import {
  AfterViewInit,
  Directive,
  EventEmitter,
  Host,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

import { DziViewerComponent } from '../../dzi-viewer.component';
import { TOsdActiveAction } from '@telespot/shared/viewers/data-access';
import { TRoiSelectionType } from '@telespot/sdk';
import OpenSeadragon from 'openseadragon';
import { DziOverlaysViewerDirective, OverlayData } from '../dzi-overlays-viewer/dzi-overlays-viewer.directive';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[tsDziOverlaysDrawer]',
  standalone: false,
})
export class DziOverlaysDrawerDirective implements AfterViewInit, OnDestroy, OnChanges {
  private static readonly MIN_ROI_SIDE_PX = 8;

  @Input() overlays: OverlayData[] = [];
  @Input() viewerMode: TOsdActiveAction = TOsdActiveAction.drawing;
  @Input() drawingMode: TRoiSelectionType = TRoiSelectionType.boundingBox;
  @Input() defaultWidth = 100;
  @Input() defaultHeight = 100;
  @Output() overlayAdded = new EventEmitter<OverlayData>();

  private startPoint: OpenSeadragon.Point | null = null;
  private tempOverlay: HTMLDivElement | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    @Host() private dziViewer: DziViewerComponent,
    @Host() private overlaysRenderer: DziOverlaysViewerDirective,
    private renderer: Renderer2
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.viewerMode && this.dziViewer?.['viewer']) {
      const previousMode = changes.viewerMode.previousValue;
      const currentMode = changes.viewerMode.currentValue;

      if (previousMode === TOsdActiveAction.drawing && currentMode !== TOsdActiveAction.drawing) {
        this.resetDrawingState();
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.overlaysRenderer) return;

    this.overlaysRenderer.overlaysRendered.pipe(takeUntil(this.destroy$)).subscribe(() => this.addOverlaysHandlers());

    const canvasTracker = this.createHandlers(this.dziViewer['viewer'].canvas);
    canvasTracker.setTracking(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private addOverlaysHandlers(): void {
    for (const overlay of this.overlays) {
      const element = document.getElementById(overlay.id);
      if (!element) continue;

      const overlayTracker = this.createHandlers(element, true);
      overlayTracker.setTracking(true);
    }
  }

  private createHandlers(sourceElement: HTMLElement, isOverlay = false): OpenSeadragon.MouseTracker {
    return new OpenSeadragon.MouseTracker({
      element: sourceElement,

      clickHandler: (event: any) => {
        if (!this.isDrawingModeActive()) return;

        const position = isOverlay ? this.getCanvasRelativePosition(event, sourceElement) : event.position;

        if (!this.isPointWithinImageBounds(position)) return this.resetDrawingState();

        if (this.isPOIMode()) {
          this.emitPOI(position);
        }

        if (this.isROIMode()) {
          if (!this.startPoint || !this.tempOverlay) return;
          this.emitROI();
          this.resetDrawingState();
        }
      },

      pressHandler: (event: any) => {
        if (!this.isDrawingModeActive()) return;

        const viewer = this.dziViewer['viewer'];
        viewer.setMouseNavEnabled(false);

        if (!this.isROIMode()) return;

        const position = isOverlay ? this.getCanvasRelativePosition(event, sourceElement) : event.position;

        if (!this.isPointWithinImageBounds(position)) return this.resetDrawingState();

        this.startPoint = viewer.viewport.pointFromPixel(position, true);

        this.tempOverlay = this.renderer.createElement('div');
        this.renderer.setAttribute(this.tempOverlay, 'id', 'temp');
        this.renderer.setStyle(this.tempOverlay, 'border', '2px dashed white');
        this.renderer.setStyle(this.tempOverlay, 'position', 'absolute');

        viewer.addOverlay(this.tempOverlay, this.startPoint);
      },

      dragHandler: (event: any) => {
        if (!this.startPoint || !this.tempOverlay) return;

        const viewer = this.dziViewer['viewer'];
        const position = isOverlay ? this.getCanvasRelativePosition(event, sourceElement) : event.position;

        const currentPoint = viewer.viewport.pointFromPixel(position, true);
        const x = Math.min(this.startPoint.x, currentPoint.x);
        const y = Math.min(this.startPoint.y, currentPoint.y);
        const width = Math.abs(currentPoint.x - this.startPoint.x);
        const height = Math.abs(currentPoint.y - this.startPoint.y);

        const rect = new OpenSeadragon.Rect(x, y, width, height);
        viewer.updateOverlay(this.tempOverlay, rect);
      },

      dragEndHandler: () => {
        if (!this.startPoint || !this.tempOverlay) return;
        this.emitROI();
        this.resetDrawingState();
      },
    });
  }

  private isDrawingModeActive(): boolean {
    return this.viewerMode === TOsdActiveAction.drawing;
  }

  private isROIMode(): boolean {
    return this.drawingMode === TRoiSelectionType.boundingBox;
  }

  private isPOIMode(): boolean {
    return this.drawingMode === TRoiSelectionType.center;
  }

  private emitPOI(position: OpenSeadragon.Point) {
    const viewer = this.dziViewer['viewer'];
    const viewportPoint = viewer.viewport.pointFromPixel(position, true);
    const imagePoint = viewer.viewport.viewportToImageCoordinates(viewportPoint);
    this.overlayAdded.emit({
      id: '',
      x: imagePoint.x,
      y: imagePoint.y,
      w: 0,
      h: 0,
    });
  }

  private emitROI() {
    const viewer = this.dziViewer['viewer'];
    const element = viewer.getOverlayById(this.tempOverlay?.id);
    const bounds = element?.getBounds(viewer.viewport);
    if (!bounds) return;

    const imageRect = viewer.viewport.viewportToImageRectangle(bounds);
    const isClickWithoutDrag = imageRect.width === 0 && imageRect.height === 0;

    if (isClickWithoutDrag) {
      imageRect.x -= this.defaultWidth / 2;
      imageRect.y -= this.defaultHeight / 2;
      imageRect.width = this.defaultWidth;
      imageRect.height = this.defaultHeight;
    } else {
      const minSide = Math.min(imageRect.width, imageRect.height);
      if (minSide < DziOverlaysDrawerDirective.MIN_ROI_SIDE_PX) {
        return;
      }
    }

    this.overlayAdded.emit({
      id: '',
      x: imageRect.x,
      y: imageRect.y,
      w: imageRect.width,
      h: imageRect.height,
    });
  }

  private resetDrawingState() {
    this.dziViewer['viewer'].removeOverlay(this.tempOverlay);
    this.tempOverlay = null;
    this.startPoint = null;
    this.dziViewer['viewer'].setMouseNavEnabled(true);
  }

  private getCanvasRelativePosition(event: any, overlayElement: HTMLElement): OpenSeadragon.Point {
    const canvasRect = this.dziViewer['viewer'].canvas.getBoundingClientRect();
    const overlayRect = overlayElement.getBoundingClientRect();

    const offsetX = overlayRect.left - canvasRect.left;
    const offsetY = overlayRect.top - canvasRect.top;

    const x = event.position.x + offsetX;
    const y = event.position.y + offsetY;

    return new OpenSeadragon.Point(x, y);
  }

  private isPointWithinImageBounds(position: OpenSeadragon.Point): boolean {
    const viewer = this.dziViewer['viewer'];

    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return false;

    const viewportPoint = viewer.viewport.pointFromPixel(position, true);
    const imagePoint = viewer.viewport.viewportToImageCoordinates(viewportPoint);
    const imageBounds = tiledImage.source.dimensions;

    return imagePoint.x >= 0 && imagePoint.x <= imageBounds.x && imagePoint.y >= 0 && imagePoint.y <= imageBounds.y;
  }
}
